import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mark } from '@/components/Mark';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { useAppDispatch } from '@/Redux/store';
import { login } from '@/Redux/Slices/authSlice';
import { useAuth } from '@/Hooks/useAuth';

export default function Login() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation() as { state?: { from?: string } };
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');

  if (isAuthenticated) return <Navigate to={location.state?.from ?? '/'} replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(login({ identifier, password }));
    if (login.rejected.match(result)) toast.error(String(result.payload));
  };

  return (
    /*
      The one screen that is allowed to be brand-forward: nothing is being
      operated yet, so the mark can be 56px and carry its tabletop ring. Past
      this door the identity drops to a 26px rail badge and the amber is
      rationed to the primary action and the focus ring.
    */
    <div className="grid min-h-dvh place-items-center bg-[var(--background)] p-4">
      <div className="w-full max-w-[22rem]">
        <div className="mb-6 flex flex-col items-center text-center">
          <Mark size={56} />
          <h1 className="mt-3 text-lg font-semibold tracking-tight">Tarabeeza</h1>
          <p className="eyebrow mt-1 text-[var(--muted-foreground)]">
            {t('platform.appName')}
          </p>
        </div>

        <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-5">
          <p className="mb-4 text-xs leading-relaxed text-[var(--muted-foreground)]">
            {t('platform.auth.signInHint')}
          </p>

          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1">
              <label
                htmlFor="login-identifier"
                className="block text-xs font-medium text-[var(--muted-foreground)]"
              >
                {t('auth.email')}
              </label>
              <Input
                id="login-identifier"
                type="text"
                autoComplete="username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
              />
            </div>

            <div className="space-y-1">
              <label
                htmlFor="login-password"
                className="block text-xs font-medium text-[var(--muted-foreground)]"
              >
                {t('auth.password')}
              </label>
              <Input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? t('common.loading') : t('auth.login')}
            </Button>
          </form>
        </div>

        <div className="mt-4 flex items-center justify-center gap-2">
          <ThemeSwitcher />
          <LanguageSwitcher />
        </div>
      </div>
    </div>
  );
}
